// Reception dashboard — route shell (RON-5469).
//
// OPENS IN ITS OWN TAB. The prototype is built that way — `rcClose()` calls `window.close()` and
// `rcProfile()` calls `window.opener.focus()` — and the sidebar link opens it with `target`
// rather than routing in place. So this screen renders NO app Sidebar and no house header: it is
// a full-width desk display with its own chrome.
//
// Contract (READ-ONLY — the Laravel team's folder):
//   D:\laragon\www\laravel-migration\docs\RON-5460\RON-5460_frontend-reception.md
// Prototype:
//   D:\laragon\www\visitor-management-prototype_updated_2\prototypes\reception-dashboard.html
//
// Phase R1 draws the board — one `GET /visitor/reception/board` paints the whole thing. The
// detail panel, walk-in dialog, check-in/out and the PIN lock are R2-R4 and are deliberately not
// stubbed here: a reception screen with buttons that only toast is worse than one without them.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";
import ReceptionDateBar from "./components/ReceptionDateBar.jsx";
import ReceptionFilterTabs from "./components/ReceptionFilterTabs.jsx";
import ReceptionList from "./components/ReceptionList.jsx";
import ReceptionStats from "./components/ReceptionStats.jsx";
import ReceptionTopBar from "./components/ReceptionTopBar.jsx";
import ReceptionDetailPanel from "./components/ReceptionDetailPanel.jsx";
import ReceptionFreshness from "./components/ReceptionFreshness.jsx";
import ReceptionNotifyModal from "./components/ReceptionNotifyModal.jsx";
import ReceptionApprovalAlert from "./components/ReceptionApprovalAlert.jsx";
import ReceptionApprovalModal from "./components/ReceptionApprovalModal.jsx";
import ReceptionLockScreen from "./components/ReceptionLockScreen.jsx";
import ReceptionWalkInModal from "./components/ReceptionWalkInModal.jsx";
import {
  ApproveEntryApproval_api,
  CheckInVisit_api,
  DenyEntryApproval_api,
  CheckOutVisit_api,
  CreateWalkIn_api,
  NotifyHost_api,
  SetVisitVip_api,
  UnlockReception_api,
  fetchAllBoardRows,
  fetchReceptionBoard,
  fetchReceptionSiteCounts,
  fetchReceptionSites,
  isSuccessfulResponse,
  toUiResponse,
} from "./ReceptionAPI.jsx";
import { newIdempotencyKey } from "../Visitor/visitorApiMapper.js";
import ronspotBlueLogo from "../../assets/images/ronspot-blue-logo.svg";
import { buildCsv, downloadCsv, exportFileName } from "./receptionExport.js";
import {
  DEFAULT_PER_PAGE,
  nextPollDelay,
  RECEPTION_FILTER,
  getRelativeDay,
} from "./receptionConstants.js";
import useVisitorAccess from "../Visitor/useVisitorAccess.js";

// Same reasoning as the visitor list: one request per letter is several round trips for one
// intention. A front desk types names constantly, so this matters more here, not less.
const SEARCH_DEBOUNCE_MS = 350;

// The site the desk last used. A receptionist opens this screen every morning and it is always
// the same building — making them re-pick it daily would be a chore the screen could have
// remembered. Per-browser, which is per-desk, which is the right scope.
const SITE_STORAGE_KEY = "ronspot.reception.siteId";

// The idle timeout is NOT ours to choose any more.
//
// §7b (A22) added `settings.inactivity_lock_minutes` to the board and asked us plainly to "stop
// hardcoding it" — an admin sets it between 1 and 480 minutes, and a timeout the screen invents is
// one they cannot change. The mapper supplies 30 only when the field is absent entirely.

const readStoredSite = () => {
  try {
    return localStorage.getItem(SITE_STORAGE_KEY) || null;
  } catch {
    // Private mode, or storage blocked by policy. Not worth failing a page load over.
    return null;
  }
};

const storeSite = (siteId) => {
  try {
    if (siteId) localStorage.setItem(SITE_STORAGE_KEY, siteId);
  } catch {
    /* ignore — see readStoredSite */
  }
};

const ReceptionUI = () => {
  const { t } = useTranslation("common");
  const { canReception } = useVisitorAccess();
  const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);

  const [sites, setSites] = useState([]);
  // Starts NULL, never from storage.
  //
  // Seeding it from localStorage meant the board request fired with the remembered id before the
  // site list had come back to validate it — which is how a fixture id left over from the demo
  // data reached the real API and came back "That site could not be found". The stored value is
  // read below, once there is a list to check it against.
  const [siteId, setSiteId] = useState(null);

  const [board, setBoard] = useState(null);
  // When WE received the current board. The footer measures from this rather than from the
  // server's `as_at` stamp, so it needs no agreement about clocks or timezones — see
  // `relativeFromMs` in receptionConstants.js for why that mattered.
  const [fetchedAt, setFetchedAt] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Null until the first board answers. Sending no date lets the server default to today (§3),
  // which is the right question to ask — the browser does not know what day it is where the site
  // is, and this screen may well be in a different timezone from the server.
  const [date, setDate] = useState(null);
  // §4b — the switcher's per-site headcounts. NOT loaded with the page: see `loadSiteCounts`.
  const [siteCounts, setSiteCounts] = useState(null);
  const [isLoadingSiteCounts, setIsLoadingSiteCounts] = useState(false);
  const [filter, setFilter] = useState(RECEPTION_FILTER.ALL);
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [page, setPage] = useState(1);
  // Whichever date the server last told us was today (§3 returns `is_today` on every board). The
  // first load defaults to today, so this is known from the first response onwards.
  //
  // Kept so the date bar and the stat tenses never have to ask the BROWSER what day it is: that is
  // a different machine’s clock, it can be a day out, and `is_today` exists precisely so we do not
  // have to guess.
  const [serverToday, setServerToday] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  // Remembers a deliberate close of the detail panel, so the auto-select further down does not
  // immediately undo it.
  //
  // STATE, not a ref. As a ref, clearing it on a new query changed nothing the auto-select effect
  // was watching, so the panel stayed shut until something else happened to re-run the effect.
  const [panelDismissed, setPanelDismissed] = useState(false);

  useEffect(() => {
    document.title = `${t("react_reception_title")} | Ronspot`;
  }, [t]);

  // ── Sites ──────────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canReception) return;

    let cancelled = false;

    (async () => {
      try {
        const { ok, response, sites: list } = await fetchReceptionSites();
        if (cancelled) return;

        if (!ok) {
          // Surfaced, not swallowed. Without the site list there is no board to draw, and a
          // silent failure here is what let a stale id reach the server and come back as a
          // confusing "site could not be found".
          handleApiUiResponse(response, { successToaster: 0, errorToaster: 1, alertPopup: 1 });
          setSiteId(null);

          return;
        }

        setSites(list);

        const remembered = readStoredSite();

        // THE REMEMBERED SITE IS ONLY A SUGGESTION. It is validated against the list the server
        // actually returned before it is used — a desk that browsed the demo data has a fixture
        // id in localStorage, and sending that to the real API is exactly the 404 we hit.
        //
        // Falling back to the first site rather than leaving the board unscoped: `site_id` is
        // required on every call, so "no site" is not a state this screen can render.
        setSiteId(
          remembered && list.some((site) => site.id === remembered)
            ? remembered
            : list[0]?.id ?? null
        );
      } catch (error) {
        console.error("GetReceptionSites error", error);
        // Same reasoning as the refusal above — a thrown request must not leave a stale id live.
        if (!cancelled) setSiteId(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canReception]);

  useEffect(() => storeSite(siteId), [siteId]);

  // ── The board ──────────────────────────────────────────────────────────────────────────────
  //
  // The in-flight token is the same guard the visitor list uses: a receptionist changes site,
  // date, tab and search constantly, and those responses can land out of order. Without it, a
  // slow answer for yesterday can overwrite today's board — on the screen that says who is in the
  // building.
  const requestToken = useRef(0);


  const loadBoard = useCallback(
    async ({ nextPage = 1, append = false, ...params }) => {
      if (!params.siteId) return;

      const token = ++requestToken.current;

      try {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);

        const { ok, response, board: loaded } = await fetchReceptionBoard({
          SiteID: params.siteId,
          Date: params.date,
          Filter: params.filter,
          Query: params.term,
          Page: nextPage,
          PerPage: DEFAULT_PER_PAGE,
        });

        if (token !== requestToken.current) return;

        if (!ok) {
          handleApiUiResponse(response, { successToaster: 0, errorToaster: 1, alertPopup: 1 });
          return;
        }

        setBoard(loaded);
        setFetchedAt(Date.now());
        if (loaded.isToday && loaded.date) setServerToday(loaded.date);
        setRows((current) => (append ? [...current, ...loaded.items] : loaded.items));
        setPage(loaded.page);
      } catch (error) {
        console.error("GetReceptionBoard error", error);
      } finally {
        if (token === requestToken.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    // `handleApiUiResponse` is stable for the lifetime of the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Clearing the box is "show me everyone again" and is applied immediately; anything else waits
  // for the typing to stop.
  useEffect(() => {
    const term = query.trim();

    if (term === "") {
      setAppliedQuery("");
      return undefined;
    }

    const timer = setTimeout(() => setAppliedQuery(term), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  // One effect owns "something about the query changed, start again from page 1". Site, date,
  // filter and search are four narrowing conditions on ONE request, so they share one trigger
  // rather than each firing their own fetch and racing.
  useEffect(() => {
    if (!canReception || !siteId) return;

    setSelectedId(null);
    setPanelDismissed(false);
    loadBoard({ siteId, date, filter, term: appliedQuery, nextPage: 1, append: false });
  }, [canReception, siteId, date, filter, appliedQuery, loadBoard]);

  const hasMore = board?.hasMore === true;

  // ── Auto-refresh (§9) ──────────────────────────────────────────────────────────────────────
  //
  // Mirrors of the current query, so `refreshBoard` below can read them without listing them as
  // dependencies. If it depended on `page`, its identity would change every time somebody pressed
  // "load more" and the poll timer would restart underneath them.
  const pageRef = useRef(1);
  const isRefreshing = useRef(false);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // A QUIET re-read of the board. Everything about it is chosen so that a desk nobody is touching
  // stays exactly as the receptionist left it:
  //
  //   · no `setIsLoading` — the list must not flash a spinner once a minute
  //   · no `setSelectedId(null)` — the open detail panel stays open, on the same person
  //   · no toaster on failure — a dropped request mid-shift is not an event worth an alert; the
  //     footer's "updated N minutes ago" is already the honest signal, and the next tick retries
  //   · it re-reads EVERY PAGE THE USER HAS LOADED, not just the first. Refreshing page 1 alone
  //     would silently collapse a 400-row board back to 200 every minute — the rows would vanish
  //     from under somebody mid-shift, which is a worse bug than the staleness it fixes.
  //
  // It shares `requestToken` with `loadBoard`, so a poll that is in flight when the user changes
  // site, date, filter or search is discarded rather than landing on top of the newer answer.
  const refreshBoard = useCallback(async () => {
    if (!siteId) return;
    // Never stack. A slow network must not queue a minute's worth of requests.
    if (isRefreshing.current) return;

    isRefreshing.current = true;
    const token = ++requestToken.current;

    try {
      const collected = [];
      let latest = null;
      let lastPage = 1;

      for (let p = 1; p <= Math.max(1, pageRef.current); p += 1) {
        // eslint-disable-next-line no-await-in-loop
        const { ok, board: loaded } = await fetchReceptionBoard({
          SiteID: siteId,
          Date: date,
          Filter: filter,
          Query: appliedQuery,
          Page: p,
          PerPage: DEFAULT_PER_PAGE,
        });

        // Abandoned without touching state: the user has asked a different question since.
        if (token !== requestToken.current) return;
        if (!ok) return;

        latest = loaded;
        lastPage = p;
        collected.push(...loaded.items);

        // The board shrank while we were away — fewer pages than the user had loaded.
        if (!loaded.hasMore) break;
      }

      if (token !== requestToken.current || !latest) return;

      setBoard(latest);
      setFetchedAt(Date.now());
      if (latest.isToday && latest.date) setServerToday(latest.date);
      setRows(collected);
      setPage(lastPage);
    } catch (error) {
      // Swallowed on purpose. This runs unattended, on a timer; the next tick is the retry.
      console.error("Reception board refresh failed", error);
    } finally {
      isRefreshing.current = false;
    }
  }, [siteId, date, filter, appliedQuery]);

  // §4b — the switcher's per-site headcounts, FETCHED WHEN THE PANEL OPENS, not with the page.
  //
  // The backend was explicit about why it is its own call rather than a block on the sites list:
  // the sites answer once at login, so a count carried on it would be frozen at page load, and a
  // badge reading "47 on site" could be hours old by the time somebody opened the switcher. On a
  // screen that doubles as an evacuation muster, a stale headcount is worse than none.
  //
  // So it is re-fetched on EVERY open. That is one small request per deliberate user action, and
  // the numbers are true at the moment they are read.
  //
  // A failure is NOT toastered. This runs from opening a dropdown, not from pressing a button that
  // promised something, and the switcher already says "not available" for a count it does not
  // have. An alert over the whole screen because a badge failed would be louder than the fact.
  const loadSiteCounts = useCallback(() => {
    // Guarded rather than queued: a double-click on the trigger is one intent, not two.
    if (isLoadingSiteCounts) return;

    setIsLoadingSiteCounts(true);

    (async () => {
      // The board's day, so the badge and the board describe the same date — a multi-day visitor
      // counts on every day they cover, on both sides. `date` is null until the first board
      // answers, and omitting it lets the server default to today, which is the same day the
      // board defaulted to.
      const { ok, counts } = await fetchReceptionSiteCounts(date ? { Date: date } : {});

      setIsLoadingSiteCounts(false);
      // Left at its previous value on failure rather than blanked. Numbers that were true a
      // moment ago, beside an `as_at` that says when, beat an empty row.
      if (ok) setSiteCounts(counts);
    })();
  }, [date, isLoadingSiteCounts]);

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;

    loadBoard({
      siteId,
      date,
      filter,
      term: appliedQuery,
      nextPage: page + 1,
      append: true,
    });
  }, [isLoading, isLoadingMore, hasMore, loadBoard, siteId, date, filter, appliedQuery, page]);

  // ── Writes (§6, §7, §8) ────────────────────────────────────────────────────────────────────
  //
  // Every write carries an `Idempotency-Key`, minted per ACTION. §8 puts it plainest for this
  // screen: "there is a person standing there waiting" — so a receptionist who sees no instant
  // reply presses again, and without a key that registers two people.
  //
  // The key is minted at the press rather than held per flow (as the invite wizard does), because
  // each press here IS the whole action: there is no multi-step form to retry within.
  const [isBusy, setIsBusy] = useState(false);
  const [notifyRow, setNotifyRow] = useState(null);

  // The approval dialog is open or it is not — there is no row to carry, because the banner is
  // about ONE request at a time and the dialog reads the same board data the banner does. A
  // board poll landing mid-decision therefore updates the dialog rather than leaving it showing
  // a list the server no longer agrees with — which is the 422 their contract describes.
  //
  // Holds the APPROVAL ID rather than a boolean, since Phase H made this a queue: the dialog has
  // to say WHICH request it is deciding, and the id is the one stable handle across a refresh.
  const [openApprovalId, setOpenApprovalId] = useState(null);

  // Derived, never copied into state. The board is the single source for both the banner and
  // the dialog, so a poll moves them together and neither can show a request the server has
  // already decided.
  const approvals = board?.entryApprovals ?? [];
  const openApproval = approvals.find((item) => item.id === openApprovalId) ?? null;

  const runWrite = useCallback(
    async (label, call) => {
      try {
        setIsBusy(true);

        const response = await call(newIdempotencyKey());

        // The success toast is ON for these. Unlike the invite flows, nothing navigates after a
        // check-in — the board just changes underneath — so without a toast the receptionist has
        // no confirmation that anything happened. A 409's reason (the contractor gate) reaches
        // them through the same helper as an alert popup, which is what §6 asks for: "show these,
        // don't summarise them".
        // `toUiResponse` rewrites the walk-in endpoint’s 201 to a 200 for this helper only —
        // it branches on `code === 200` exactly, and would otherwise show an error toast for a
        // walk-in that registered perfectly well.
        handleApiUiResponse(toUiResponse(response), {
          successToaster: 1,
          errorToaster: 1,
          alertPopup: 1,
        });

        if (!isSuccessfulResponse(response)) return false;

        // Re-read rather than patching the row locally: a check-in moves counts, tabs and the
        // overstay flag, and reconstructing all of that on the client is how the board drifts
        // away from the server.
        await loadBoard({ siteId, date, filter, term: appliedQuery, nextPage: 1, append: false });

        return true;
      } catch (error) {
        console.error(`${label} error`, error);

        return false;
      } finally {
        setIsBusy(false);
      }
    },
    [handleApiUiResponse, loadBoard, siteId, date, filter, appliedQuery]
  );

  const checkIn = useCallback(
    (row) =>
      runWrite("CheckInVisit", (key) =>
        CheckInVisit_api({ VisitID: row.id, SiteID: siteId, IdempotencyKey: key })
      ),
    [runWrite, siteId]
  );

  const checkOut = useCallback(
    (row) =>
      runWrite("CheckOutVisit", (key) =>
        CheckOutVisit_api({ VisitID: row.id, SiteID: siteId, IdempotencyKey: key })
      ),
    [runWrite, siteId]
  );

  const toggleVip = useCallback(
    (row) =>
      runWrite("SetVisitVip", (key) =>
        SetVisitVip_api({
          VisitID: row.id,
          SiteID: siteId,
          // Sent explicitly rather than as a toggle the server works out: re-flagging an
          // already-flagged VIP is a 200 saying so (§7), which is only harmless because the
          // intended state travels with the request.
          IsVip: !row.isVip,
          IdempotencyKey: key,
        })
      ),
    [runWrite, siteId]
  );

  const sendNotification = useCallback(
    async ({ row, channel, message, subject }) => {
      const ok = await runWrite("NotifyHost", (key) =>
        NotifyHost_api({
          VisitID: row.id,
          SiteID: siteId,
          Channel: channel,
          Message: message,
          Subject: subject,
          IdempotencyKey: key,
        })
      );

      // Left open on failure, with the text intact — retyping a message because a send failed is
      // a small insult at a busy desk.
      if (ok) setNotifyRow(null);
    },
    [runWrite, siteId]
  );

  // ── Entry approvals — Phase H (see ReceptionAPI.jsx) ───────────────────────────────────────
  //
  // Both go through `runWrite`, so they get the idempotency key, the toast, the alert popup for
  // a refusal and the board re-read that every other write here gets.
  //
  // THE IDEMPOTENCY KEY IS NOT DECORATION HERE. The same request can be decided at the kiosk,
  // behind a staff PIN, at the same moment — so a double press and a race are both real, and the
  // second decider gets a 409 that `runWrite` surfaces in the server's own words.
  //
  // THE DIALOG CLOSES ON A REFUSAL TOO, which is the opposite of what it did before. A 409 means
  // somebody else decided and a 422 means the outstanding list changed underneath; in both cases
  // what is on screen describes a moment that has passed, and keeping it open invites the
  // receptionist to press again against stale data. The board re-read that follows redraws the
  // queue, and if the request is still there they can open it afresh.
  const decideEntry = useCallback(
    (label, call) =>
      runWrite(label, call).then((ok) => {
        setOpenApprovalId(null);

        return ok;
      }),
    [runWrite]
  );

  const approveEntry = useCallback(
    ({ pending, reason, note, acknowledged }) =>
      decideEntry("ApproveEntryApproval", (key) =>
        ApproveEntryApproval_api({
          ApprovalID: pending.id,
          SiteID: siteId,
          ReasonCode: reason,
          ReasonNote: note,
          AcknowledgedItemIDs: acknowledged,
          IdempotencyKey: key,
        })
      ),
    [decideEntry, siteId]
  );

  const denyEntry = useCallback(
    ({ pending, reason, note }) =>
      decideEntry("DenyEntryApproval", (key) =>
        DenyEntryApproval_api({
          ApprovalID: pending.id,
          SiteID: siteId,
          ReasonCode: reason,
          ReasonNote: note,
          IdempotencyKey: key,
        })
      ),
    [decideEntry, siteId]
  );

  // ── The lock (§7b, R4) ─────────────────────────────────────────────────────────────────────
  //
  // §9: "the PIN lock itself is now built ... What is still open is the *session*: how long an
  // unlock lasts before the screen relocks is your call, on your side." So the idle timeout is
  // ours, and 30 minutes matches the prototype.
  //
  // The PIN itself is NEVER checked here — see ReceptionLockScreen.jsx.
  const [isLocked, setIsLocked] = useState(false);
  const [lockError, setLockError] = useState(null);
  const [lockedForSeconds, setLockedForSeconds] = useState(0);

  const currentSite = useMemo(
    () => sites.find((site) => site.id === siteId) || null,
    [sites, siteId]
  );

  // Whether a PIN exists that will open this desk.
  //
  // Since A22 the PIN is the COMPANY's shared staff PIN, not the site's, and a paired Android
  // kiosk can change it — so it can change without anyone touching this screen. The board's copy
  // is therefore the fresher one: it arrives on every poll, where the sites call answered once at
  // login. The site's flag is the fallback for an API that has not shipped A22 yet.
  //
  // With no PIN there is nothing that could unlock the screen, so locking it would strand the
  // desk — the control is refused rather than offered and then found useless.
  const canLock = board?.settings?.receptionPinSet ?? currentSite?.receptionPinSet === true;

  const lockNow = useCallback(() => {
    if (!canLock) return;

    setLockError(null);
    setLockedForSeconds(0);
    setIsLocked(true);
  }, [canLock]);

  const unlock = useCallback(
    async (pin) => {
      try {
        setIsBusy(true);

        const response = await UnlockReception_api({ SiteID: siteId, Pin: pin });

        if (isSuccessfulResponse(response)) {
          setIsLocked(false);
          setLockError(null);
          setLockedForSeconds(0);

          return;
        }

        // The server's wording, shown as-is. A 403 reads the same whether the PIN was wrong or
        // the site has none — it deliberately never says which.
        setLockError(response?.Data?.ErrorMessage || t("react_reception_lock_failed"));
        // Present only on the 409 after five wrong attempts.
        setLockedForSeconds(Number(response?.Data?.Records?.locked_for_seconds) || 0);
      } catch (error) {
        console.error("UnlockReception error", error);
        setLockError(t("react_reception_lock_failed"));
      } finally {
        setIsBusy(false);
      }
    },
    [siteId, t]
  );

  // Idle auto-lock. The listeners are passive and the timer is only reset, never re-created per
  // event, so a desk screen left scrolling does not churn.
  useEffect(() => {
    if (!canReception || !canLock || isLocked) return undefined;

    const idleMs = (board?.settings?.idleLockMinutes ?? 30) * 60 * 1000;
    let timer = setTimeout(lockNow, idleMs);

    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(lockNow, idleMs);
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((event) => document.addEventListener(event, reset, { passive: true }));

    return () => {
      clearTimeout(timer);
      events.forEach((event) => document.removeEventListener(event, reset));
    };
  }, [canReception, canLock, isLocked, lockNow, board?.settings?.idleLockMinutes]);

  // The timer. `setTimeout` that re-arms itself rather than `setInterval`, for two reasons: the
  // delay is re-jittered on every tick, and a slow refresh cannot have the next one fire on top
  // of it.
  //
  // PAUSED while the screen is locked and while the tab is hidden. A locked desk is an unattended
  // one, and a background tab has nobody reading it — polling either is load spent on a screen
  // nobody can see. Both resume below, with an immediate catch-up read rather than a wait.
  useEffect(() => {
    if (!canReception || !siteId || isLocked) return undefined;

    let timer = null;

    const tick = () => {
      if (document.visibilityState === "visible") refreshBoard();

      timer = setTimeout(tick, nextPollDelay());
    };

    timer = setTimeout(tick, nextPollDelay());

    return () => clearTimeout(timer);
  }, [canReception, siteId, isLocked, refreshBoard]);

  // Coming back to the screen. Waiting up to 75 seconds for the next tick would mean somebody
  // returning from lunch reads a board from before they left — the one moment staleness is most
  // likely and least expected.
  //
  // Covers both doors in: switching back to the tab, and unlocking the screen. `isLocked` is in
  // the dependency list, so unlocking re-runs this effect and the catch-up read fires once.
  useEffect(() => {
    if (!canReception || !siteId || isLocked) return undefined;

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshBoard();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [canReception, siteId, isLocked, refreshBoard]);

  // The unlock catch-up, kept separate from the effect above so it fires ONCE on the transition
  // rather than on every re-run of it. Without the ref this would also fire on first mount, a
  // second read immediately after the first load.
  const wasLocked = useRef(false);

  useEffect(() => {
    if (wasLocked.current && !isLocked) refreshBoard();

    wasLocked.current = isLocked;
  }, [isLocked, refreshBoard]);


  const [isWalkInOpen, setIsWalkInOpen] = useState(false);

  const registerWalkIn = useCallback(
    async (fields) => {
      const ok = await runWrite("CreateWalkIn", (key) =>
        CreateWalkIn_api({
          ...fields,
          SiteID: siteId,
          // The board's date, which is always today while the button is available — see the
          // `is_today` guard on the control itself.
          Date: board?.date,
          IdempotencyKey: key,
        })
      );

      // Left open on failure with the fields intact: re-typing a walk-in's details because a
      // send failed, while they stand at the desk, is the worst moment to lose a form.
      if (ok) setIsWalkInOpen(false);
    },
    [runWrite, siteId, board]
  );

  // ── Auto-selecting the first row ───────────────────────────────────────────────────────────
  //
  // The panel is a third of the screen; leaving it reading "select a visitor" on load wastes it,
  // and the prototype opens with the first row selected.
  //
  // But it must not FIGHT the receptionist. Closing the panel is a deliberate act — usually to
  // see the full-width board — and a re-selection a tick later would make the close button look
  // broken. So a dismissal is remembered until the query changes, at which point the rows on
  // screen are different and the old intent no longer applies.

  // CLOSING COLLAPSES THE PANEL, it does not empty it. The prototype adds `detail-hidden` to the
  // split, which drops the grid to one column and hides the panel outright — the receptionist gets
  // the full-width board, which is the only reason to press this button.
  //
  // Ours used to clear `selectedId` instead, so the 380px column stayed exactly where it was and
  // simply swapped to "Select a visitor". The board never widened, which is why the button looked
  // like it did nothing.
  //
  // The SELECTION IS KEPT, also like the prototype: reopening returns you to the person you were
  // reading rather than to an empty panel.
  const dismissPanel = useCallback(() => {
    setPanelDismissed(true);
  }, []);

  const reopenPanel = useCallback(() => {
    setPanelDismissed(false);
  }, []);

  // The three detail-panel actions with no endpoint behind them — reprint badge, edit details and
  // manual override. They are shown rather than hidden so the desk can see what the screen WILL do
  // one day, but pressing one has to say plainly that it does not work yet.
  //
  // Routed through the app's own popup rather than a bare `alert()`: it is the same surface every
  // other refusal on this screen uses, so it carries the branding and does not block the tab.
  // 409 puts it in the orange popup band — "valid request, refused by state", which is exactly
  // what this is.
  const showUnavailable = useCallback(
    (messageKey) => {
      handleApiUiResponse(
        { Data: { ResponseCode: 409, ErrorMessage: t(messageKey) } },
        { successToaster: 0, errorToaster: 0, alertPopup: 1 }
      );
    },
    // `handleApiUiResponse` and `t` are stable for the lifetime of the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Clicking any row reopens it, exactly as the prototype does — a receptionist who closed the
  // panel to scan the board and then picks somebody plainly wants to see them.
  const selectRow = useCallback((row) => {
    setSelectedId(row.id);
    setPanelDismissed(false);
  }, []);

  // Auto-select the first row, and RE-select it when the current selection is no longer in the
  // list.
  //
  // The second half is the bug this used to have, and it is a race rather than an oversight.
  // Changing filter does three things at once — clear the selection, clear the dismissal, start a
  // new fetch — but the fetch is async, so for one render the screen holds `selectedId: null`
  // alongside the OLD rows. This effect fired on that render and selected the old first row. The
  // new rows then arrived, the effect saw a selection and returned early, and `selectedRow` came
  // back `null` because that id was not in the new set. Rows on the left, empty panel on the
  // right — exactly what pressing a tab looked like.
  //
  // Asking whether the selection still EXISTS, rather than whether there is one, closes it: the
  // stale id fails that test the moment the new rows land.
  useEffect(() => {
    if (panelDismissed || !rows.length) return;
    if (selectedId && rows.some((row) => row.id === selectedId)) return;

    setSelectedId(rows[0].id);
  }, [rows, selectedId, panelDismissed]);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectedId) || null,
    [rows, selectedId]
  );

  const siteName = useMemo(
    () => board?.site?.name || sites.find((site) => site.id === siteId)?.name || "",
    [board, sites, siteId]
  );

  // ── Export (R5) ────────────────────────────────────────────────────────────────────────────
  //
  // Walks every page matching the current date, filter and search, rather than exporting the rows
  // on screen. Exporting the visible page would hand somebody a file that looks complete and is
  // not — the same failure as a muster that stops at 200.
  const [isExporting, setIsExporting] = useState(false);

  const exportBoard = useCallback(async () => {
    if (!siteId || isExporting) return;

    try {
      setIsExporting(true);

      const { ok, response, rows: allRows, complete } = await fetchAllBoardRows({
        SiteID: siteId,
        Date: date,
        Filter: filter,
        Query: appliedQuery,
      });

      if (!ok) {
        handleApiUiResponse(toUiResponse(response), {
          successToaster: 0,
          errorToaster: 1,
          alertPopup: 1,
        });

        return;
      }

      downloadCsv(
        exportFileName({ siteName, date: board?.date || date, filter }),
        buildCsv(allRows)
      );

      // Said out loud rather than shipped as a short file. `complete` is false only if the page
      // guard tripped, which would mean a paging bug — but a truncated export that says nothing
      // is worse than one that does.
      if (!complete) {
        handleApiUiResponse(
          { Data: { ResponseCode: 400, ErrorMessage: t("react_reception_export_truncated") } },
          { successToaster: 0, errorToaster: 1, alertPopup: 0 }
        );
      }
    } catch (error) {
      console.error("Export error", error);
    } finally {
      setIsExporting(false);
    }
    // `handleApiUiResponse` is stable for the lifetime of the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, date, filter, appliedQuery, siteName, board, isExporting, t]);

  // ── Access gate ────────────────────────────────────────────────────────────────────────────
  //
  // `canReception` is one of the five host capabilities (§3 of the invite contract) and this
  // screen is behind it. Default is OFF, so an unknown entitlement shows the unavailable state
  // rather than a board the server would refuse anyway.
  if (!canReception) {
    return (
      <div className="reception-page reception-page--closed">
        <p className="reception-unavailable">{t("react_reception_unavailable")}</p>
      </div>
    );
  }

  return (
    <div className="reception-page">
      {/* The capped, centred card. The overlays below are siblings of it, not children, so a
          dialog is never clipped by the card’s width. */}
      <div className="reception-app">
        <ReceptionTopBar
        sites={sites}
        siteId={siteId}
        onSiteChange={setSiteId}
        // The board’s own counts. They cover the CURRENT site only, but they are already on
        // screen in the tiles above, so that one row can show real numbers the instant the panel
        // opens rather than a spinner. `siteCounts` replaces them for every row once it lands.
        counts={board?.counts}
        // §4b — every site’s headcount, and the request that fetches it. Passed as a callback
        // rather than a loaded value because the picker is what knows when it opened.
        siteCounts={siteCounts}
        isLoadingSiteCounts={isLoadingSiteCounts}
        onSitePickerOpen={loadSiteCounts}
        // R4. Rendered but inert until `POST /visitor/reception/unlock` is wired — the PIN has to
        // be checked server-side, because the browser still holds a valid session while the
        // screen is locked, so a check done in JavaScript is one a passer-by skips by reloading.
        canLock={canLock}
        onLock={lockNow}
      />

      <main className="reception-main">
        <ReceptionDateBar
          date={board?.date || date}
          isToday={board?.isToday ?? false}
          todayISO={serverToday}
          onDateChange={setDate}
        />

        {/* Between the date bar and the stats, where the prototype puts it: under the bar that
            says which day is on screen, because the banner is only ever about today — and above
            the tiles, because a person is waiting in the lobby and the tiles are not urgent.

            Draws nothing when the list is empty, which is every board at a site whose
            `unvetted_policy` is `off` as well as every quiet lobby. */}
        <ReceptionApprovalAlert
          approvals={approvals}
          onReview={() => setOpenApprovalId(approvals[0]?.id ?? null)}
        />

        <ReceptionStats
          counts={board?.counts}
          isLoading={isLoading && !board}
          relativeDay={getRelativeDay(board?.date, board?.isToday, serverToday)}
        />

        <section className="reception-controls">
          <ReceptionFilterTabs
            filter={filter}
            counts={board?.counts}
            onFilterChange={setFilter}
          />

          {/* The prototype's `.controls-right`: search, export and walk-in are one group pinned to
              the right at 10px apart, not three siblings spread across a `space-between` row. */}
          <div className="reception-controls__right">
          <div className="reception-search">
            {/* Decorative — the placeholder already says what the box is for. */}
            <svg
              className="reception-search__icon"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              className="reception-search__input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("react_reception_search_placeholder")}
              aria-label={t("react_reception_search_placeholder")}
            />
            {query.trim() !== appliedQuery && (
              <span className="reception-search__status">{t("react_loading")}</span>
            )}
          </div>

          {/* Only on today's board. A walk-in registers AND checks somebody in (§5) — they are
              standing at the desk — so registering one against yesterday or next Tuesday would
              put a person on site on a day they were not there, in the record that doubles as
              the fire log. Disabled rather than hidden, with the reason in the tooltip, so the
              control's absence is never mistaken for a rendering fault. */}
          {/* Exports everything matching the current date, filter and search — not the rows on
              screen. Built in the browser, so it is NOT the audited export §9 lists as still
              parked; the button says “Download CSV” rather than claiming more than it does. */}
          <button
            type="button"
            className="reception-btn reception-btn--ghost"
            disabled={isExporting || !rows.length}
            onClick={exportBoard}
          >
            {!isExporting && (
              <svg
                className="reception-btn__icon"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            )}
            {t(isExporting ? "react_loading" : "react_reception_export")}
          </button>

          <button
            type="button"
            className="reception-btn reception-btn--accent"
            disabled={!board?.isToday || isBusy}
            title={board?.isToday ? undefined : t("react_reception_walk_in_today_only")}
            onClick={() => setIsWalkInOpen(true)}
          >
            {t("react_reception_walk_in_button")}
          </button>
          </div>
        </section>

        {/* The board and the detail panel side by side, as the prototype has them. The panel
            renders the row the list already holds — §4 is explicit that it is the same shape, so
            selecting somebody costs no second request. */}
        <div
          className={`reception-split${panelDismissed ? " reception-split--full" : ""}`}
        >
          <ReceptionList
            rows={rows}
            isLoading={isLoading}
            // The row menu runs the same handlers the detail panel does, so an action taken from
            // either place behaves identically — including the board re-read afterwards.
            isBusy={isBusy}
            onCheckIn={checkIn}
            onCheckOut={checkOut}
            onNotifyHost={setNotifyRow}
            onToggleVip={toggleVip}
            onUnavailable={showUnavailable}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            onLoadMore={loadMore}
            query={query}
            selectedId={selectedId}
            onSelect={selectRow}
            siteName={siteName}
            total={board?.total ?? 0}
          />

          {/* The prototype's reopen handle (`#detail-reopen`), shown only while the panel is
              closed. It was parked before, on the reasoning that "clicking any row reopens it, so
              a floating handle is a control for nothing" — that reasoning assumed closing did
              nothing to the layout. Now that closing genuinely collapses the panel, the handle has
              a job the row click cannot do: restore the panel WITHOUT changing who is selected.
              It is also the only way back on an empty board, where there is no row to click. */}
          {panelDismissed && (
            <button
              type="button"
              className="reception-detail-reopen"
              onClick={reopenPanel}
              title={t("react_reception_show_details_panel")}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
              {t("react_reception_details_handle")}
            </button>
          )}

          {!panelDismissed && (
          <ReceptionDetailPanel
            row={selectedRow}
            isBusy={isBusy}
            onClose={dismissPanel}
            onCheckIn={checkIn}
            onCheckOut={checkOut}
            onToggleVip={toggleVip}
            onNotifyHost={setNotifyRow}
            onUnavailable={showUnavailable}
          />
          )}
        </div>
      </main>


      <footer className="reception-foot">
        {/* Vendored, like the white one in the top bar — same reasoning: a desk screen that runs
            all day should not depend on a third-party fetch for its own branding. This is the
            blue variant (#1B468B, the same value as --r-blue), for the white footer. */}
        <span className="reception-foot__brand">
          <img className="reception-foot__logo" src={ronspotBlueLogo} alt="Ronspot" />
          {t("react_reception_footer")}
        </span>
        {/* `as_at` is the server's clock at the read — nothing is cached, and §9 says to poll for
            now, so the desk needs to be able to see how stale the board is. */}
        <ReceptionFreshness asAt={board?.asAt} fetchedAt={fetchedAt} />
      </footer>
      </div>

      {/* Looked up by id on every render rather than held in state, so a board poll keeps the
          dialog in step with the server — including the case their 422 is about, where an admin
          approves one of the documents while the dialog is open.

          It also means a request decided elsewhere simply vanishes from under the dialog, which
          is the honest outcome: there is nothing left to decide. */}
      <ReceptionApprovalModal
        pending={openApproval}
        isBusy={isBusy}
        onApprove={approveEntry}
        onDeny={denyEntry}
        onClose={() => setOpenApprovalId(null)}
      />

      <ReceptionNotifyModal
        row={notifyRow}
        isBusy={isBusy}
        onSend={sendNotification}
        onClose={() => setNotifyRow(null)}
      />

      <ReceptionWalkInModal
        siteName={siteName}
        siteTimezone={currentSite?.timezone}
        isOpen={isWalkInOpen}
        isBusy={isBusy}
        onSubmit={registerWalkIn}
        onClose={() => setIsWalkInOpen(false)}
      />

      {/* Last, so it paints over everything including the open dialogs. */}
      <ReceptionLockScreen
        isLocked={isLocked}
        siteName={siteName}
        error={lockError}
        lockedForSeconds={lockedForSeconds}
        isBusy={isBusy}
        onUnlock={unlock}
      />

      {renderApiUi()}
    </div>
  );
};

export default ReceptionUI;
